import findIndex from "lodash/findIndex";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import { useAppContextController } from "context/AppContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropTypes from "prop-types";

import useCompanyCache from "hooks/useCompanyCache";

import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Alert, Button, Snackbar } from "@mui/material";
import useGetBackButton from "hooks/useGetBackButton";
import useHandleBackButtonMainPage from "hooks/useHandleBackButtonMainPage";
import useSessionAuth from "hooks/useSessionAuth";
import { isEmpty } from "lodash";
import generateFiltersFromString from "utils/generateFiltersFromString";
import { EventContextProvider } from "context/EventContext";
import InterviewsFilterCards from "../events/components/InterviewsFilterCards";
import EventsTable from "../events/components/EventsTable";
import InterviewEditor from "../events/components/InterviewEditor";
import InterviewApplicants from "../events/components/InterviewApplicants";
import InterviewPreviewModal from "../events/components/InterviewPreviewModal";
import InterviewsInformation from "./components/InterviewsInformation";
import InterviewActivities from "../events/components/EventActivities";
import InterviewContent from "./components/InterviewContent";
import InterviewAttendees from "./components/InterviewAttendees";
import InterviewNotes from "./components/InterviewNotes";
import InterviewAttachments from "./components/InterviewAttachments";
import InterviewAdditionalInformation from "./components/InterviewAdditionalInformation";

function Interviews({ mode = "edit" }) {
  const navigate = useNavigate();

  const {
    currentEvent,
    setCurrentEvent,
    currentLoggedUser,
    venues,
    setVenues,
    userType,
    isMobile,
    currentApplicant,
    company,
    setCompany,
    setCompanyType,
  } = useAppContextController();

  const { isLoadingCompany, refetchCompany } = useCompanyCache({
    company,
    setCompany,
    setCompanyType,
  });

  const isCompany = company?.companyType === "Company";
  const isNotUser = userType !== "User";

  const [searchParams] = useSearchParams();
  const timeFrame = searchParams.get("timeFrame");
  const { eventUrl, action, venueSlug } = useParams();
  const [actionSelected, setActionSelected] = useState(action || "");

  // Get back button generated by other screens
  const { backParam, backToTitle, backToUrl, backButton } = useGetBackButton();

  const setNavigateUrl = (url) => navigate(url);

  const [selectedPanel, setSelectedPanel] = useState("activities");
  const [toastAlert, setToastAlert] = useState({
    isShow: false,
    message: "",
  });
  const [filters, setFilters] = useState({
    timeFrame: timeFrame || "Current",
    eventType: "Interview",
  });
  const [eventPreview, setEventPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [interviewPreview, setInterviewPreview] = useState(null);
  const [previewContent, setPreviewContent] = useState("all");
  const [mongoEventId, setMongoEventId] = useState(null);

  const refetchEventsFuncRef = useRef(null);

  const bindRefetchEvents = (callback) => {
    refetchEventsFuncRef.current = callback;
  };

  const refetchEvents = refetchEventsFuncRef.current;

  const toggleEventPreviewModal = (isOpen) => {
    setOpen(isOpen);
  };

  const eventListNavigateFuncRef = useRef(null);
  const bindEventListNavigateFunc = (func) => {
    eventListNavigateFuncRef.current = func;
  };

  // useEffect(() => {
  //   if (panel) setSelectedPanel(panel);
  // }, [panel]);

  // Handle back button for this screen
  const {
    backListUrl,
    filterParam,
    pageParam,
    subFilterParam,
    subPageParam,
    pageFromUrl,
    filtersFromUrl,
    subPageFromUrl,
    subFiltersFromUrl,
    navigateToListWithBack,
    navigateToScreenWithBack,
    navigateToScreenDirectlyWithBack,
  } = useHandleBackButtonMainPage({
    baseUrl: "/events",
    screenName: "Interviews",
  });

  const renderPanel = (selection) => {
    switch (selection) {
      case "activities":
        return <InterviewActivities />;
      case "additionalInfo":
        return <InterviewAdditionalInformation />;
      case "content":
        return <InterviewContent />;
      case "attendees":
        return <InterviewAttendees />;
      case "notes":
        return <InterviewNotes />;
      case "attachments":
        return <InterviewAttachments />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (filterParam != null) {
      if (filterParam) {
        const newFilters = generateFiltersFromString(filterParam);
        setFilters(newFilters);
      } else {
        setFilters({});
      }
    }
  }, [filterParam]);

  useEffect(() => {
    if (eventUrl && (actionSelected || action)) {
      setFilters({ eventUrl });
    }
    if (action) {
      setActionSelected(action);
    } else {
      setActionSelected("");
    }

    if (currentEvent) {
      setMongoEventId(currentEvent._id);
    }
  }, [eventUrl, action, venueSlug]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        open={toastAlert.isShow}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
        key="top-center"
      >
        <Alert
          onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
          // severity={toastAlert.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastAlert.message}
        </Alert>
      </Snackbar>
      <EventContextProvider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            {backToUrl ? (
              <Grid item xs={12} sm={12} md={12} pb={5} pt={3}>
                {backButton}
              </Grid>
            ) : (
              <InterviewsFilterCards
                filters={filters}
                setFilters={setFilters}
                // setPage={setPage}
              />
            )}
            <EventsTable
              eventUrl={eventUrl}
              setActionSelected={setActionSelected}
              showFiltersList
              filters={filters}
              title={company?.companyType === "Venue" ? "Events" : "Interviews"}
              setFilters={setFilters}
              setNavigateUrl={setNavigateUrl}
              defaultSort="eventDate"
              // setOpen={setOpen}
              parent="Events"
              setOpen={toggleEventPreviewModal}
              setEventPreview={setEventPreview}
              setInterviewPreview={setInterviewPreview}
              setPreviewContent={setPreviewContent}
              bindRefetchEvents={bindRefetchEvents}
              setToastAlert={setToastAlert}
              isInterview={company?.companyType === "Company"}
              parentPage={pageFromUrl}
              parentNavigateWithBack={navigateToListWithBack}
              parentNavigateToScreenWithBack={navigateToScreenDirectlyWithBack}
              bindNavigateFunc={bindEventListNavigateFunc}
            />
          </Grid>
          {backListUrl && (
            <Grid item xs={12} sm={12} md={12} mt={2}>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  navigate(backListUrl);
                }}
              >
                Back to Interview List
              </Button>
            </Grid>
          )}
          {["eventsinfo", "eventsall", ""].includes(actionSelected) &&
            (eventUrl || mode === "create") && (
              <Grid item xs={12} sm={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <InterviewsInformation
                      mode={mode}
                      open={open}
                      selectedPanel={selectedPanel}
                      onPanelSelect={setSelectedPanel}
                      setOpen={toggleEventPreviewModal}
                      setInterviewPreview={setInterviewPreview}
                      setPreviewContent={setPreviewContent}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    {mode === "edit" && renderPanel(selectedPanel)}
                  </Grid>
                </Grid>
              </Grid>
            )}
          {/* {["eventsinfo", "eventsall", ""].includes(actionSelected) &&
            (eventUrl || mode === "create") &&
            isNotUser && (
              <>
                <Grid item xs={12} sm={12}>
                  <InterviewEditor
                    setOpen={toggleEventPreviewModal}
                    setInterviewPreview={setInterviewPreview}
                    setPreviewContent={setPreviewContent}
                  />
                </Grid>
              </>
            )} */}
          {["eventsroster", "eventsall"].includes(actionSelected) && eventUrl && isNotUser && (
            <>
              <Grid item xs={12} sm={12}>
                <InterviewApplicants
                  currentEvent={currentEvent}
                  showFiltersList={false}
                  fetchAll
                  showSearchBar={false}
                  staffingPoolName={currentEvent?.title}
                  setToastAlert={setToastAlert}
                  parentPage={subPageFromUrl}
                  parentNavigateWithBack={navigateToScreenWithBack}
                />
              </Grid>
            </>
          )}
          {interviewPreview && (
            <InterviewPreviewModal
              open={open}
              setOpen={setOpen}
              interviewPreview={interviewPreview}
              setInterviewPreview={setInterviewPreview}
              content={previewContent}
            />
          )}
        </Grid>
        {/* <AddANoteModal
          currentApplicant={currentEvent}
          open={notesModalOpen}
          toastAlert={toastAlert}
          setToastAlert={setToastAlert}
          setOpen={setNoteModalOpen}
          saveNote={saveNote}
        /> */}
      </EventContextProvider>
    </DashboardLayout>
  );
}

// Setting default values for the props
Interviews.defaultProps = {
  mode: "edit",
};

// Typechecking props
Interviews.propTypes = {
  mode: PropTypes.string,
};
export default Interviews;
